// Central treasury's side of reconciliation: record money that ARRIVED, match it to what
// departments DECLARED, undo a match, and work through exceptions. Money is ALWAYS integer ngwee.
//
// The database enforces the rules (admin only; matched totals can never exceed either side; a
// match is never deleted, only undone with a reason). These functions ask; its plain-language
// refusals are shown as written. Ranking suggestions is pure and lives in src/lib/matching.ts.
import { supabase } from '@/lib/supabase'
import { currentUserId } from './auth'
import { DataError, unwrap } from './errors'
import type { PaymentMethod } from './dues'
import type { MovementStatus } from './remittances'
import type { OpenReceipt, OpenRemittance } from '@/lib/matching'
import { fetchAllRows } from './paging'

// ---------------------------------------------------------------- what arrived
export interface Receipt extends OpenReceipt {
  method: PaymentMethod
  bankRef: string | null
  statementLineRef: string | null
  departmentName: string | null
  note: string | null
  status: 'open' | 'partially_allocated' | 'allocated' | 'cancelled'
}

type AckRow = {
  id: string
  received_amount_ngwee: number
  method: PaymentMethod
  received_on: string
  bank_ref: string | null
  bank_ref_norm: string | null
  statement_line_ref: string | null
  counterparty_department_id: string | null
  note: string | null
  status: Receipt['status']
  department: { name: string } | null
}

/** Sum of the ACTIVE matches (not undone) per receipt and per remittance. */
async function activeMatchTotals(): Promise<{ byAck: Map<string, number>; byMovement: Map<string, number> }> {
  // Every active match in the church, forever: page through them rather than trust one capped answer.
  const res = await fetchAllRows((f, t) => supabase.from('cash_movement_match').select('id, movement_id, ack_id, amount_ngwee').is('unmatched_at', null).order('id').range(f, t))
  const rows = unwrap(res as never, 'Could not load the matches.') as { movement_id: string; ack_id: string; amount_ngwee: number }[]
  const byAck = new Map<string, number>()
  const byMovement = new Map<string, number>()
  for (const r of rows) {
    byAck.set(r.ack_id, (byAck.get(r.ack_id) ?? 0) + Number(r.amount_ngwee))
    byMovement.set(r.movement_id, (byMovement.get(r.movement_id) ?? 0) + Number(r.amount_ngwee))
  }
  return { byAck, byMovement }
}

/** Money central treasury has recorded as received, newest first (cancelled ones left out). */
export async function listReceipts(opts: { onlyOpen?: boolean } = {}): Promise<Receipt[]> {
  const [res, totals] = await Promise.all([
    fetchAllRows((f, t) => {
      let q = supabase
        .from('cash_movement_ack')
        .select('id, received_amount_ngwee, method, received_on, bank_ref, bank_ref_norm, statement_line_ref, counterparty_department_id, note, status, department:department(name)')
        .eq('direction', 'dept_to_central')
        .neq('status', 'cancelled')
      if (opts.onlyOpen) q = q.in('status', ['open', 'partially_allocated'])
      return q.order('received_on', { ascending: false }).order('id').range(f, t)
    }),
    activeMatchTotals(),
  ])
  return (unwrap(res as never, 'Could not load the money received.') as AckRow[]).map((r) => ({
    id: r.id,
    departmentId: r.counterparty_department_id,
    departmentName: r.department?.name ?? null,
    receivedNgwee: Number(r.received_amount_ngwee),
    allocatedNgwee: totals.byAck.get(r.id) ?? 0,
    receivedOn: r.received_on,
    refNorm: r.bank_ref_norm,
    method: r.method,
    bankRef: r.bank_ref,
    statementLineRef: r.statement_line_ref,
    note: r.note,
    status: r.status,
  }))
}

export interface ReceiptInput {
  amountNgwee: number
  method: PaymentMethod
  receivedOn: string
  bankRef?: string
  statementLineRef?: string
  /** Leave empty when nobody has claimed it yet. */
  departmentId?: string | null
  note?: string
}

const clean = (s?: string | null) => (s && s.trim() ? s.trim() : null)

/**
 * Record money that arrived (central treasury only). Returns the receipt's id. `clientRef` makes a
 * retry whose first answer was lost find the receipt it already made instead of making a second one.
 */
export async function recordReceipt(i: ReceiptInput, clientRef: string): Promise<string> {
  if (!Number.isSafeInteger(i.amountNgwee) || i.amountNgwee <= 0) {
    throw new DataError({ code: 'P0001', message: 'The amount must be greater than zero.' })
  }
  const userId = await currentUserId()
  const fields = {
    received_amount_ngwee: i.amountNgwee,
    method: i.method,
    received_on: i.receivedOn,
    bank_ref: clean(i.bankRef),
    statement_line_ref: clean(i.statementLineRef),
    counterparty_department_id: i.departmentId || null,
    note: clean(i.note),
  }
  const res = await supabase
    .from('cash_movement_ack')
    .insert({ direction: 'dept_to_central', ...fields, recorded_by: userId, client_ref: clientRef })
    .select('id')
    .single()
  if (res.error) {
    if (res.error.code === '23505') {
      const prior = await supabase.from('cash_movement_ack').select('id').eq('recorded_by', userId).eq('client_ref', clientRef).maybeSingle()
      if (prior.data) {
        // The first try reached the server but its answer was lost; apply what is on the screen now
        // (the database refuses once something has been matched against it, and that is fine here).
        const id = (prior.data as { id: string }).id
        await supabase.from('cash_movement_ack').update(fields).eq('id', id)
        return id
      }
    }
    throw new DataError(res.error, 'Could not record the money received.')
  }
  return (res.data as { id: string }).id
}

// ---------------------------------------------------------------- what departments declared
export interface OpenMovement extends OpenRemittance {
  departmentName: string
  method: PaymentMethod
  bankRef: string | null
  handedTo: string | null
  status: MovementStatus
}

type MovementRow = {
  id: string
  department_id: string
  declared_amount_ngwee: number
  moved_on: string
  bank_ref: string | null
  bank_ref_norm: string | null
  method: PaymentMethod
  handed_to: string | null
  status: MovementStatus
  department: { name: string } | null
}

/** Remittances departments declared that are not fully confirmed yet, oldest first. */
export async function listOpenRemittances(): Promise<OpenMovement[]> {
  const [res, totals] = await Promise.all([
    fetchAllRows((f, t) =>
      supabase
        .from('cash_movement')
        .select('id, department_id, declared_amount_ngwee, moved_on, bank_ref, bank_ref_norm, method, handed_to, status, department:department(name)')
        .eq('direction', 'dept_to_central')
        .in('status', ['declared', 'partially_settled', 'disputed'])
        .order('moved_on')
        .order('id')
        .range(f, t),
    ),
    activeMatchTotals(),
  ])
  return (unwrap(res as never, 'Could not load the remittances.') as MovementRow[]).map((r) => ({
    id: r.id,
    departmentId: r.department_id,
    departmentName: r.department?.name ?? '',
    declaredNgwee: Number(r.declared_amount_ngwee),
    matchedNgwee: totals.byMovement.get(r.id) ?? 0,
    movedOn: r.moved_on,
    refNorm: r.bank_ref_norm,
    method: r.method,
    bankRef: r.bank_ref,
    handedTo: r.handed_to,
    status: r.status,
  }))
}

// ---------------------------------------------------------------- matching
export interface Match {
  id: string
  movementId: string
  ackId: string
  departmentId: string
  amountNgwee: number
  matchType: 'auto_exact' | 'auto_ref' | 'manual'
  matchedAt: string
}

/** Active matches touching a remittance or a receipt. */
export async function listMatches(by: { movementId: string } | { ackId: string }): Promise<Match[]> {
  const q = supabase
    .from('cash_movement_match')
    .select('id, movement_id, ack_id, department_id, amount_ngwee, match_type, matched_at')
    .is('unmatched_at', null)
  const res = await ('movementId' in by ? q.eq('movement_id', by.movementId) : q.eq('ack_id', by.ackId)).order('matched_at')
  return (
    unwrap(res as never, 'Could not load the matches.') as {
      id: string; movement_id: string; ack_id: string; department_id: string; amount_ngwee: number; match_type: Match['matchType']; matched_at: string
    }[]
  ).map((r) => ({
    id: r.id,
    movementId: r.movement_id,
    ackId: r.ack_id,
    departmentId: r.department_id,
    amountNgwee: Number(r.amount_ngwee),
    matchType: r.match_type,
    matchedAt: r.matched_at,
  }))
}

/** Match some or all of a received deposit to a declared remittance. */
export async function createMatch(m: { movementId: string; ackId: string; departmentId: string; amountNgwee: number }): Promise<void> {
  if (!Number.isSafeInteger(m.amountNgwee) || m.amountNgwee <= 0) {
    throw new DataError({ code: 'P0001', message: 'The amount must be greater than zero.' })
  }
  const userId = await currentUserId()
  const res = await supabase.from('cash_movement_match').insert({
    movement_id: m.movementId,
    ack_id: m.ackId,
    department_id: m.departmentId,
    amount_ngwee: m.amountNgwee,
    match_type: 'manual',
    matched_by: userId,
  })
  if (res.error) throw new DataError(res.error, 'Could not match them.')
}

/** Undo a match. Nothing is deleted: it is marked undone, with who did it and why. */
export async function unmatch(matchId: string, reason: string): Promise<void> {
  if (!reason.trim()) throw new DataError({ code: 'P0001', message: 'Give a reason for undoing the match.' })
  const userId = await currentUserId()
  const res = await supabase
    .from('cash_movement_match')
    .update({ unmatched_at: new Date().toISOString(), unmatched_by: userId, unmatch_reason: reason.trim() })
    .eq('id', matchId)
    .is('unmatched_at', null)
    .select('id')
  if (res.error) throw new DataError(res.error, 'Could not undo the match.')
  if (((res.data as unknown[]) ?? []).length === 0) {
    throw new DataError({ code: 'P0001', message: 'That match has already been undone. Refresh the page.' })
  }
}

// ---------------------------------------------------------------- exceptions
export type ExceptionKind =
  | 'unmatched_movement' | 'unmatched_ack' | 'short_receipt' | 'over_receipt' | 'stale_partial'
  | 'missing_proof' | 'late_remittance' | 'excess_cash_on_hand' | 'duplicate_ack'
export type ExceptionState = 'open' | 'investigating' | 'resolved' | 'waived'

export const EXCEPTION_LABELS: Record<ExceptionKind, string> = {
  unmatched_movement: 'Declared but nothing received',
  unmatched_ack: 'Money received that nobody has claimed',
  short_receipt: 'Less received than declared',
  over_receipt: 'More received than declared',
  stale_partial: 'Only partly confirmed for too long',
  missing_proof: 'Photo missing',
  late_remittance: 'Cash collected but not sent on',
  excess_cash_on_hand: 'Department holding too much cash',
  duplicate_ack: 'Same deposit recorded twice',
}

export interface ReconException {
  id: string
  kind: ExceptionKind
  departmentId: string | null
  departmentName: string | null
  varianceNgwee: number | null
  state: ExceptionState
  deptResponse: string | null
  resolution: string | null
  detectedAt: string
}

type ExRow = {
  id: string
  kind: ExceptionKind
  department_id: string | null
  variance_ngwee: number | null
  state: ExceptionState
  dept_response: string | null
  resolution: string | null
  detected_at: string
  department: { name: string } | null
}

/** Exceptions still needing attention (a department sees only its own). */
export async function listOpenExceptions(departmentId?: string): Promise<ReconException[]> {
  let q = supabase
    .from('reconciliation_exception')
    .select('id, kind, department_id, variance_ngwee, state, dept_response, resolution, detected_at, department:department(name)')
    .in('state', ['open', 'investigating'])
  if (departmentId) q = q.eq('department_id', departmentId)
  const res = await q.order('detected_at')
  return (unwrap(res as never, 'Could not load the exceptions.') as ExRow[]).map((r) => ({
    id: r.id,
    kind: r.kind,
    departmentId: r.department_id,
    departmentName: r.department?.name ?? null,
    varianceNgwee: r.variance_ngwee === null ? null : Number(r.variance_ngwee),
    state: r.state,
    deptResponse: r.dept_response,
    resolution: r.resolution,
    detectedAt: r.detected_at,
  }))
}

/** Central treasury: look for new exceptions now (it also runs every night). Returns how many are open. */
export async function runCheck(): Promise<number> {
  const res = await supabase.rpc('run_reconciliation_check')
  if (res.error) throw new DataError(res.error, 'Could not run the check.')
  return Number(res.data ?? 0)
}

/** Central treasury: close an exception with an explanation, or mark it as being looked into. */
export async function decideException(id: string, to: 'investigating' | 'resolved' | 'waived', resolution?: string): Promise<void> {
  if (to !== 'investigating' && !resolution?.trim()) {
    throw new DataError({ code: 'P0001', message: 'Write what was found or decided before closing it.' })
  }
  const res = await supabase
    .from('reconciliation_exception')
    .update({ state: to, ...(resolution?.trim() ? { resolution: resolution.trim() } : {}) })
    .eq('id', id)
    .select('id')
  if (res.error) throw new DataError(res.error, 'Could not update the exception.')
  if (((res.data as unknown[]) ?? []).length === 0) throw new DataError({ code: 'P0001', message: 'You cannot change that exception.' })
}

/** A department explains an exception (the only thing it may change on one). */
export async function respondToException(id: string, response: string): Promise<void> {
  if (!response.trim()) throw new DataError({ code: 'P0001', message: 'Write your explanation first.' })
  const res = await supabase.from('reconciliation_exception').update({ dept_response: response.trim() }).eq('id', id).select('id')
  if (res.error) throw new DataError(res.error, 'Could not save your explanation.')
  if (((res.data as unknown[]) ?? []).length === 0) throw new DataError({ code: 'P0001', message: 'You cannot reply to that one.' })
}
